import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import type { NavSection } from '@/types/nav.types';
import type { PublicUser } from '@/types/user.types';
import { useCurrentUser } from '@/hooks/useCurrentUser.hook';
import { ROUTES } from '@/routes/route.config';
import { authApi } from '@/services/api.service';
import { authStorage } from '@/services/authStorage.service';

interface UseDashboardLayoutResult {
  user: PublicUser | undefined;
  sections: NavSection[];
  activePath: string;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onCloseSidebar: () => void;
  onLogout: () => void;
}

// Items without a `to` are part of the design but not yet implemented — rendered disabled.
const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', icon: '📊', to: ROUTES.dashboard },
      { label: 'Analytics', icon: '📈', to: ROUTES.analytics },
    ],
  },
  {
    label: 'Management',
    items: [
      { label: 'User Management', icon: '👥', to: ROUTES.users },
      { label: 'Rental Management', icon: '📦', to: ROUTES.rentals },
      { label: 'Shoutt Moderation', icon: '🛡️', to: ROUTES.moderation },
      { label: 'Campus Management', icon: '🏫', to: ROUTES.campuses },
    ],
  },
  {
    label: 'Finance',
    items: [{ label: 'Payments & Revenue', icon: '💳', to: ROUTES.payments }],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Notifications', icon: '🔔', to: ROUTES.notifications },
      { label: 'Contact Messages', icon: '✉️', to: ROUTES.contactMessages },
      { label: 'Settings', icon: '⚙️', to: ROUTES.settings },
    ],
  },
];

export const useDashboardLayout = (): UseDashboardLayoutResult => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar automatically on route change — covers mobile nav link taps.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const onLogout = (): void => {
    const refreshToken = authStorage.getRefreshToken();
    const finish = (): void => {
      authStorage.clear();
      queryClient.clear();
      navigate(ROUTES.login, { replace: true });
    };

    if (!refreshToken) {
      finish();
      return;
    }
    authApi.logout({ refreshToken }).catch(() => undefined).finally(finish);
  };

  return {
    user,
    sections: NAV_SECTIONS,
    activePath: location.pathname,
    sidebarOpen,
    onToggleSidebar: () => setSidebarOpen((prev) => !prev),
    onCloseSidebar: () => setSidebarOpen(false),
    onLogout,
  };
};
